import { useId } from 'react'
import type { JSX } from 'react'

import { Button, CopyIcon, Text, toast } from '@buffer-mono/popcorn'

import { useCollaborationNotesContext } from '~publish/components/CollaborationNotes'
import { NotesFeed } from '~publish/components/Notes'

import styles from './ContentItemNotes.module.css'

export const CLOSED_THREAD_MESSAGE =
  'This draft was scheduled, so its notes are closed.'

const copyText = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text)
    toast.success('Note copied')
  } catch {
    toast.error('Unable to copy the note')
  }
}

/**
 * Takes the composer's place once a draft thread closes, as when the draft is
 * scheduled while the panel is open. A Note the account had started is never
 * sent: it stays as read-only text they can copy elsewhere.
 */
export function ClosedThreadNotice(): JSX.Element {
  const { create } = useCollaborationNotesContext()
  const unsentText = create.draft.text.trim() ? create.draft.text : null
  const captionId = useId()

  return (
    <div className={styles.closedNotice}>
      <NotesFeed.Status>{CLOSED_THREAD_MESSAGE}</NotesFeed.Status>
      {unsentText && (
        <figure className={styles.unsent} aria-labelledby={captionId}>
          <figcaption id={captionId}>
            <Text size="sm" weight="medium">
              Your unsent note
            </Text>
          </figcaption>
          <p className={styles.unsentNote}>{unsentText}</p>
          <div className={styles.unsentActions}>
            <Button
              type="button"
              size="small"
              variant="secondary"
              onClick={(): void => void copyText(unsentText)}
            >
              <CopyIcon /> Copy
            </Button>
          </div>
        </figure>
      )}
    </div>
  )
}
